import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { UserRoleSelection } from '@/components/auth/UserRoleSelection';
import { TermsAndConditions } from '@/components/auth/TermsAndConditions';
import { ProfileInformationForm } from '@/components/auth/ProfileInformationForm';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { UserRole } from '@/types/user-roles';
import { supabase } from '@/integrations/supabase/client';
import { BrandLogo } from '@/components/ui/brand-logo';

const CompleteProfile = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const metadata = (user as any)?.user_metadata || {};
  const initialRole = (metadata?.role as UserRole) || 'regular';
  const [role, setRole] = useState<UserRole>(initialRole);
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState({
    organizationName: (metadata.organization_name as string) || '',
    firstName: (metadata.first_name as string) || (metadata.given_name as string) || '',
    lastName: (metadata.last_name as string) || (metadata.family_name as string) || '',
    email: user?.email || (metadata.email as string) || '',
    phone: (metadata.phone as string) || (metadata.phone_number as string) || '',
    peoReferralCode: ''
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signup" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!profileData.organizationName.trim()) {
      toast.error('Please enter your organization name');
      return;
    }

    if (!profileData.firstName.trim()) {
      toast.error('Please enter your first name');
      return;
    }

    if (!profileData.lastName.trim()) {
      toast.error('Please enter your last name');
      return;
    }

    if (!profileData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    if (!profileData.email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the terms and privacy policy to continue');
      return;
    }

    if (!role) {
      toast.error('Please select your user type');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Update user profile with all profile data
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          user_type: role,
          first_name: profileData.firstName.trim(),
          last_name: profileData.lastName.trim(),
          phone: profileData.phone.trim(),
          organization_name: profileData.organizationName.trim(),
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update user metadata to include terms agreement
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          role: role,
          terms_agreed: true,
          terms_agreed_at: new Date().toISOString(),
          profile_completed: true,
          first_name: profileData.firstName.trim(),
          last_name: profileData.lastName.trim()
        }
      });

      if (metadataError) throw metadataError;

      // Ensure auth email is set/updated when needed
      const trimmedEmail = profileData.email.trim();
      if (!user.email || user.email.toLowerCase() !== trimmedEmail.toLowerCase()) {
        const { error: emailUpdateError } = await supabase.auth.updateUser({ email: trimmedEmail });
        if (emailUpdateError) throw emailUpdateError;
      }

      // Send verification email
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'email_verification',
            email: trimmedEmail,
            userId: user.id,
          },
        });
        toast.message(`Verification email sent to ${trimmedEmail}`);
      } catch (err) {
        console.error('Error sending verification email via Loops:', err);
      }

      // Notify admin for PEO verification requests and create representative link if provider selected
      if (role === 'peo') {
        try {
          // If user selected a specific provider, create peo_representatives record
          if (selectedProviderId) {
            await supabase.functions.invoke('create-user-peo-link', {
              body: {
                userId: user.id,
                providerId: selectedProviderId
              }
            });
          }

          await supabase.functions.invoke('send-email', {
            body: {
              type: 'peo_verification_request',
              userData: {
                email: trimmedEmail,
                firstName: profileData.firstName.trim(),
                lastName: profileData.lastName.trim(),
                role: 'peo',
                organizationName: profileData.organizationName.trim(),
                userId: user.id,
                signupDate: new Date().toISOString(),
                requestedAt: new Date().toISOString(),
                adminUrl: `${window.location.origin}/admin/peo-verifications`,
                peoReferralCode: profileData.peoReferralCode.trim() || null,
                selectedProviderId: selectedProviderId || null,
              },
            },
          });
        } catch (err) {
          console.error('Error processing PEO verification request:', err);
        }
      }

      toast.success('Profile completed successfully!');
      window.location.href = '/dashboard';
    } catch (error: any) {
      console.error('Profile completion error:', error);
      setError(error.message || 'An error occurred while completing your profile');
      toast.error(error.message || 'An error occurred while completing your profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileDataChange = (data: any) => {
    setProfileData(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Complete Your Profile
          </h2>
          <p className="mt-2 text-center text-gray-600">
            Just a couple more details to finish setting up your account
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <ProfileInformationForm 
            formData={profileData}
            onFormDataChange={handleProfileDataChange}
          />

          {role === 'peo' && (
            <div className="border-t border-border pt-6">
              <label htmlFor="peoReferralCode" className="block text-sm font-medium mb-2">
                PEO Referral Code (Optional)
              </label>
              <input
                id="peoReferralCode"
                type="text"
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                placeholder="Enter PEO referral code if you have one"
                value={profileData.peoReferralCode}
                onChange={(e) => setProfileData(prev => ({ ...prev, peoReferralCode: e.target.value }))}
              />
              <p className="text-sm text-muted-foreground mt-1">
                If you were referred by a specific PEO provider, enter their code here.
              </p>
            </div>
          )}

          <div className="border-t border-border pt-6">
            <UserRoleSelection 
              role={role}
              onRoleChange={setRole}
              selectedProviderId={selectedProviderId}
              onProviderChange={setSelectedProviderId}
            />
          </div>

          <div className="border-t border-border pt-6">
            <TermsAndConditions 
              agreedToTerms={agreedToTerms}
              onAgreedToTermsChange={setAgreedToTerms}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || !agreedToTerms}
          >
            {isSubmitting ? 'Completing Profile...' : 'Complete Profile'}
          </Button>
          
          {!agreedToTerms && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Please complete all fields and agree to the terms to continue
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;